import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  Search as SearchSdkType,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
  SearchSpec,
} from '@wix/sdk-types';
import * as ambassadorWixStoresCatalogV3InventoryItem from './stores-catalog-v3-inventory-item-inventory-items-v-3.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';
import { createSearchUtils } from '@wix/sdk-runtime/search-builder-utils';

/**
 * Inventory Item.
 *
 * Each inventory item represents a specific product variant at a specific location.
 * The combination of `variantId` and `locationId` is unique — you can't have two inventory items for the same variant at the same location.
 */
export interface InventoryItem extends InventoryItemTrackingMethodOneOf {
  /**
   * Indicates that inventory is tracked by status rather than quantity.
   *
   * When set to `true`, the item is marked as available for sale without tracking exact quantities.
   * When set to `false`, the item is marked as out of stock.
   * This tracking method is useful for made-to-order products or items with unlimited inventory.
   *
   * When using this tracking method, `trackQuantity` is `false` and preorder limits aren't supported.
   */
  inStock?: boolean;
  /**
   * Indicates that inventory is tracked by quantity.
   *
   * Set this field to the number of items currently in stock.
   * This tracking method is useful when you need to know exactly how many items are available.
   *
   * When using this tracking method, `trackQuantity` is `true`.
   * Quantity can be negative when inventory is decremented for an order that has already been paid.
   * @min -99999
   * @max 99999
   */
  quantity?: number;
  /**
   * Inventory item ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the inventory item is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the inventory item.
   *
   * Ignored when creating an inventory item.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the inventory item was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the inventory item was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Variant ID.
   * @format GUID
   * @immutable
   */
  variantId?: string;
  /**
   * Stores location ID. If not specified when creating an inventory item, the store's default location is used.
   * @format GUID
   * @immutable
   */
  locationId?: string | null;
  /**
   * Product ID.
   * @minLength 1
   * @maxLength 36
   * @immutable
   */
  productId?: string;
  /**
   * Whether the quantity is being tracked.
   * @readonly
   */
  trackQuantity?: boolean;
  /**
   * Inventory item availability status.
   * @readonly
   */
  availabilityStatus?: AvailabilityStatusWithLiterals;
  /**
   * Item preorder info.
   *
   * Preorder settings are configured per inventory item, so each variant-location combination can have its own preorder configuration.
   *
   * > **Note:** The product entity's `inventory.preorderStatus` and `inventory.preorderAvailability` fields reflect only the default location's preorder state.
   */
  preorderInfo?: PreorderInfo;
  /**
   * Associated product and variant details.
   * @readonly
   */
  product?: Product;
  /**
   * Custom field data for the inventory item object.
   *
   * [Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.
   */
  extendedFields?: ExtendedFields;
}

/** @oneof */
export interface InventoryItemTrackingMethodOneOf {
  /**
   * Indicates that inventory is tracked by status rather than quantity.
   *
   * When set to `true`, the item is marked as available for sale without tracking exact quantities.
   * When set to `false`, the item is marked as out of stock.
   * This tracking method is useful for made-to-order products or items with unlimited inventory.
   *
   * When using this tracking method, `trackQuantity` is `false` and preorder limits aren't supported.
   */
  inStock?: boolean;
  /**
   * Indicates that inventory is tracked by quantity.
   *
   * Set this field to the number of items currently in stock.
   * This tracking method is useful when you need to know exactly how many items are available.
   *
   * When using this tracking method, `trackQuantity` is `true`.
   * Quantity can be negative when inventory is decremented for an order that has already been paid.
   * @min -99999
   * @max 99999
   */
  quantity?: number;
}

export enum AvailabilityStatus {
  /** Item is out of stock. For tracked inventory, `quantity` is zero or negative and preorder isn't available. */
  OUT_OF_STOCK = 'OUT_OF_STOCK',
  /** Item is in stock and available for purchase. For tracked inventory, see the `quantity` field for the exact amount in stock. */
  IN_STOCK = 'IN_STOCK',
  /** Item is available for preorder only. Stock is zero or negative, but preorder is enabled with remaining capacity. */
  PREORDER = 'PREORDER',
}

/** @enumType */
export type AvailabilityStatusWithLiterals =
  | AvailabilityStatus
  | 'OUT_OF_STOCK'
  | 'IN_STOCK'
  | 'PREORDER';

export interface PreorderInfo {
  /**
   * Whether preorder is enabled for this inventory item.
   *
   * > **Note:** Preorder can't be enabled for digital products or products with subscriptions.
   *
   * Default: `false`
   */
  enabled?: boolean | null;
  /**
   * Message displayed to customers when the item is out of stock and preorder is enabled.
   * @minLength 1
   * @maxLength 250
   */
  message?: string | null;
  /**
   * Maximum number of items that can be preordered after stock reaches zero.
   *
   * Supported only for inventory items with `trackQuantity = true`.
   *
   * Default: `100000`
   * @max 100000
   */
  limit?: number | null;
  /**
   * Number of times this item has been preordered.
   *
   * Supported only for inventory items with `trackQuantity = true`.
   * @readonly
   * @max 99999
   */
  counter?: number | null;
  /**
   * Remaining quantity available for preorder.
   *
   * Supported only for items with `trackQuantity` set to `true`.
   * @readonly
   */
  quantity?: number | null;
}

export interface Product {
  /**
   * Product name.
   * @maxLength 80
   */
  name?: string | null;
  /**
   * List of category IDs that this product is included in directly.
   * @format GUID
   * @maxSize 2000
   */
  directCategoryIds?: string[];
  /**
   * Variant name.
   * @minLength 1
   * @maxLength 250
   */
  variantName?: string | null;
  /**
   * Variant SKU (stock keeping unit).
   * @minLength 1
   * @maxLength 40
   */
  variantSku?: string | null;
  /** Whether the variant is visible in the store. */
  variantVisible?: boolean | null;
  /**
   * Product brand.
   * @internal
   */
  brand?: Brand;
}

/** Product brand information. */
export interface Brand {
  /**
   * Brand ID.
   * @format GUID
   */
  _id?: string | null;
  /**
   * Brand name.
   * @maxLength 50
   */
  name?: string | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface InvalidateCache extends InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
  /**
   * tell us why you're invalidating the cache. You don't need to add your app name
   * @maxLength 256
   */
  reason?: string | null;
  /** Is local DS */
  localDc?: boolean;
  hardPurge?: boolean;
  /**
   * Optional caller-provided ID for tracking this invalidation through the system.
   * When set, the corresponding CDN purge completion event will include this ID,
   * allowing you to confirm when the invalidation has fully propagated.
   * Example: generate a UUID, pass it here, and later match it in the CDN purge completion event.
   * @maxLength 256
   */
  correlationId?: string | null;
}

/** @oneof */
export interface InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
}

export interface App {
  /**
   * The AppDefId
   * @minLength 1
   */
  appDefId?: string;
  /**
   * The instance Id
   * @format GUID
   */
  instanceId?: string;
}

export interface Page {
  /**
   * the msid the page is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Page ID
   * @minLength 1
   */
  pageId?: string;
}

export interface URI {
  /**
   * the msid the URI is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI path to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @minLength 1
   */
  uriPath?: string;
}

export interface File {
  /**
   * the msid the file is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by filename (for media files such as PDFs)
   * @minLength 1
   * @maxLength 256
   */
  fileName?: string;
}

export interface CustomTag {
  /**
   * the msid the tag is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Tag to invalidate by
   * @minLength 1
   * @maxLength 256
   */
  tag?: string;
}

export interface Pages {
  /**
   * the msid the pages are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by multiple Page IDs in a single message
   * @maxSize 100
   * @minLength 1
   */
  pageIds?: string[];
}

export interface URIs {
  /**
   * the msid the URIs are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI paths to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @maxSize 100
   * @minLength 1
   */
  uriPaths?: string[];
}

export interface BulkInventoryItemAction {
  /**
   * Inventory items.
   * @maxSize 1000
   */
  inventoryItems?: InventoryItem[];
  /** Type of bulk inventory changed event - created, updated or deleted */
  eventType?: EventTypeWithLiterals;
}

export enum EventType {
  UNKNOWN = 'UNKNOWN',
  CREATED = 'CREATED',
  UPDATED = 'UPDATED',
  DELETED = 'DELETED',
}

/** @enumType */
export type EventTypeWithLiterals =
  | EventType
  | 'UNKNOWN'
  | 'CREATED'
  | 'UPDATED'
  | 'DELETED';

export interface CreateInventoryItemRequest {
  /** Inventory item to create. */
  inventoryItem: InventoryItem;
}

export interface CreateInventoryItemResponse {
  /** Created inventory item. */
  inventoryItem?: InventoryItem;
}

export interface BulkCreateInventoryItemsRequest {
  /**
   * Inventory items to create.
   * @minSize 1
   * @maxSize 1000
   */
  inventoryItems: InventoryItem[];
  /**
   * Whether to return the full inventory item entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
}

export interface BulkCreateInventoryItemsResponse {
  /**
   * Inventory items created by bulk action.
   * @minSize 1
   * @maxSize 1000
   */
  results?: V3BulkInventoryItemResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface V3BulkInventoryItemResult {
  /** Bulk action metadata for inventory item. */
  itemMetadata?: ItemMetadata;
  /**
   * Full inventory item entity.
   *
   * Returned only if `returnEntity: true` is passed in the request.
   */
  item?: InventoryItem;
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @format GUID
   */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface GetInventoryItemRequest {
  /**
   * Inventory item ID.
   * @format GUID
   */
  inventoryItemId: string;
}

export interface GetInventoryItemResponse {
  /** Inventory item. */
  inventoryItem?: InventoryItem;
}

export interface UpdateInventoryItemRequest {
  /** Inventory item to update. */
  inventoryItem: InventoryItem;
  /** Reason for update. */
  reason?: ReasonTypeWithLiterals;
}

/** The reason for the inventory change. */
export enum ReasonType {
  ORDER = 'ORDER',
  MANUAL = 'MANUAL',
  REVERT_INVENTORY_CHANGE = 'REVERT_INVENTORY_CHANGE',
}

/** @enumType */
export type ReasonTypeWithLiterals =
  | ReasonType
  | 'ORDER'
  | 'MANUAL'
  | 'REVERT_INVENTORY_CHANGE';

export interface UpdateInventoryItemResponse {
  /** Updated inventory item. */
  inventoryItem?: InventoryItem;
}

/** Report when Inventory item stock status changed from in stock to out of stock and the opposite. */
export interface InventoryItemStockStatusUpdatedEvent {
  /** Inventory item new status. */
  inStock?: boolean;
}

export interface InventoryItemUpdatedWithReason {
  /** Updated inventory item. */
  currentEntity?: InventoryItem;
  /** Reason for the update. */
  reason?: ReasonTypeWithLiterals;
  /**
   * ID of the app that updated this inventory item.
   *
   * When Wix Stores updates an inventory item, value will always be: `"215238eb-22a5-4c36-9e7b-e7c08025e04e"`.
   * @format GUID
   */
  appId?: string;
}

export interface BulkUpdateInventoryItemsRequest {
  /**
   * Inventory items to update.
   * @minSize 1
   * @maxSize 1000
   */
  inventoryItems: MaskedInventoryItem[];
  /**
   * Whether to return the full inventory item entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
  /** Reason for update. */
  reason?: ReasonTypeWithLiterals;
}

export interface MaskedInventoryItem {
  /** Inventory item to update. */
  inventoryItem?: InventoryItem;
}

export interface BulkUpdateInventoryItemsResponse {
  /**
   * Inventory items updated by bulk action.
   * @minSize 1
   * @maxSize 1000
   */
  results?: V3BulkInventoryItemResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkUpsertInventoryItemsRequest {
  /**
   * Inventory items to create or update.
   * @minSize 1
   * @maxSize 1000
   */
  inventoryItems: MaskedInventoryItem[];
  /**
   * Whether to return the full inventory item entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
}

export interface BulkUpsertInventoryItemsResponse {
  /**
   * Inventory items created or updated by the bulk action.
   * @minSize 1
   * @maxSize 1000
   */
  results?: BulkUpsertInventoryItemResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkUpsertInventoryItemResult {
  /** Bulk action metadata for the inventory item. */
  itemMetadata?: ItemMetadata;
  /**
   * Full inventory item entity.
   *
   * Returned only if `returnEntity: true` is passed in the request.
   */
  item?: InventoryItem;
}

export interface BulkUpdateInventoryItemsByFilterRequest {
  /** Filter object. */
  filter: Record<string, any> | null;
  /** Inventory item to update. */
  inventoryItem: InventoryItem;
  /** Free text to match in searchable fields. */
  search?: SearchDetails;
}

export interface SearchDetails {
  /** Search mode. Defines the search logic for combining multiple terms in the `expression`. */
  mode?: ModeWithLiterals;
  /**
   * Search term or expression.
   * @maxLength 100
   */
  expression?: string | null;
  /**
   * Fields to search in. If the array is empty, all searchable fields are searched. Use dot notation to specify a JSON path. For example, `order.address.streetName`.
   * @maxLength 200
   * @maxSize 20
   */
  fields?: string[];
  /** Whether to enable the search function to use an algorithm to automatically find results that are close to the search expression, such as typos and declensions. */
  fuzzy?: boolean;
}

export enum Mode {
  /** At least one of the search terms must be present. */
  OR = 'OR',
  /** All search terms must be present. */
  AND = 'AND',
}

/** @enumType */
export type ModeWithLiterals = Mode | 'OR' | 'AND';

export interface BulkUpdateInventoryItemsByFilterResponse {
  /**
   * Job ID.
   *
   * Pass this ID to [Get Async Job](https://dev.wix.com/docs/api-reference/business-management/async-job/introduction) to retrieve job details and metadata.
   * @format GUID
   */
  jobId?: string;
}

export interface DeleteInventoryItemRequest {
  /**
   * Inventory item ID.
   * @format GUID
   */
  inventoryItemId: string;
}

export interface DeleteInventoryItemResponse {}

export interface BulkDeleteInventoryItemsRequest {
  /**
   * IDs of inventory items to delete.
   * @format GUID
   * @minLength 1
   * @maxLength 36
   * @minSize 1
   * @maxSize 1000
   */
  inventoryItemIds: string[];
}

export interface BulkDeleteInventoryItemsResponse {
  /**
   * Inventory items deleted by bulk action.
   * @minSize 1
   * @maxSize 1000
   */
  results?: BulkInventoryItemResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkInventoryItemResult {
  /** Bulk action metadata for inventory item. */
  itemMetadata?: ItemMetadata;
}

export interface BulkDeleteInventoryItemsByFilterRequest {
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter: Record<string, any> | null;
  /** Free text to match in searchable fields. */
  search?: SearchDetails;
}

export interface BulkDeleteInventoryItemsByFilterResponse {
  /**
   * Job ID.
   *
   * Pass this ID to [Get Async Job](https://dev.wix.com/docs/api-reference/business-management/async-job/introduction) to retrieve job details and metadata.
   * @format GUID
   */
  jobId?: string;
}

export interface QueryInventoryItemsRequest {
  /** Query options. */
  query?: QueryV2;
}

export interface QueryV2 extends QueryV2PagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#sorting).
   * @maxSize 10
   */
  sort?: Sorting[];
}

/** @oneof */
export interface QueryV2PagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface Paging {
  /**
   * Number of items to load.
   * @max 1000
   */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 1000
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryInventoryItemsResponse {
  /**
   * List of inventory items.
   * @minSize 1
   * @maxSize 1000
   */
  inventoryItems?: InventoryItem[];
  /** Paging metadata. */
  pagingMetadata?: PlatformPagingMetadataV2;
}

export interface PlatformPagingMetadataV2 {
  /** The number of items returned in this response. */
  count?: number | null;
  /** Cursors to navigate through result pages. Returned if cursor paging was used. */
  cursors?: CommonCursors;
}

export interface CommonCursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface SearchInventoryItemsRequest {
  /** Search options. */
  search?: CursorSearch;
}

export interface CursorSearch extends CursorSearchPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * List of sort objects.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#sorting).
   * @maxSize 10
   */
  sort?: Sorting[];
  /**
   * Logical groupings of data into facets, with summaries for each facet. For example, use aggregations to allow site visitors to narrow down their search results by selecting specific categories.
   * @maxSize 10
   */
  aggregations?: Aggregation[];
  /** Free text to match in searchable fields. */
  search?: SearchDetails;
  /**
   * Time zone to adjust date-time-based filters and aggregations, in ISO 8601 (including offsets) or IANA time zone database (including time zone IDs) format.
   * Applies to all relevant filters and aggregations, unless overridden by providing timestamps including time zone. For example, "2023-12-20T10:52:34.795Z".
   * @maxLength 50
   */
  timeZone?: string | null;
}

/** @oneof */
export interface CursorSearchPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
}

export interface Aggregation extends AggregationKindOneOf {
  /** A value aggregation calculates metrics such as count for specific fields within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number (count) of products for each price listed in the store. */
  value?: ValueAggregation;
  /** A range aggregation calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. For example, use a range aggregation to compare the number of reservations made for parties of 4 or less to the number of reservations made for parties with 5 or more. */
  range?: RangeAggregation;
  /** A scalar aggregation calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`. */
  scalar?: ScalarAggregation;
  /** A date histogram calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.) For example, use a date histogram to find how many reservations have been made at a restaurant each week. */
  dateHistogram?: DateHistogramAggregation;
  /** A nested aggregation is applied within the results of another aggregation. Rather than aggregating directly on the primary dataset, first group data using one aggregation and then apply another aggregation within each group. It allows for more complex analyses where you can summarize data at different levels of detail or hierarchy. For example, to get the number of products that are in stock and out of stock for each price listed, first perform a value aggregation on `discountedPriceNumeric`, and a second value aggregation on `inStock`. */
  nested?: NestedAggregation;
  /**
   * Aggregation name, returned in `aggregations.results.name`.
   * @maxLength 100
   */
  name?: string | null;
  /** Type of aggregation to perform. Must align with the corresponding aggregation field. */
  type?: AggregationTypeWithLiterals;
  /**
   * Field to aggregate by. Use dot notation to specify a JSON path. For example, `order.address.streetName`.
   * @maxLength 200
   */
  fieldPath?: string;
  /**
   * Deprecated. Use `nested` instead.
   * @deprecated Deprecated. Use `nested` instead.
   * @replacedBy kind.nested
   * @targetRemovalDate 2024-03-30
   */
  groupBy?: GroupByAggregation;
}

/** @oneof */
export interface AggregationKindOneOf {
  /** A value aggregation calculates metrics such as count for specific fields within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number (count) of products for each price listed in the store. */
  value?: ValueAggregation;
  /** A range aggregation calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. For example, use a range aggregation to compare the number of reservations made for parties of 4 or less to the number of reservations made for parties with 5 or more. */
  range?: RangeAggregation;
  /** A scalar aggregation calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`. */
  scalar?: ScalarAggregation;
  /** A date histogram calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.) For example, use a date histogram to find how many reservations have been made at a restaurant each week. */
  dateHistogram?: DateHistogramAggregation;
  /** A nested aggregation is applied within the results of another aggregation. Rather than aggregating directly on the primary dataset, first group data using one aggregation and then apply another aggregation within each group. It allows for more complex analyses where you can summarize data at different levels of detail or hierarchy. For example, to get the number of products that are in stock and out of stock for each price listed, first perform a value aggregation on `discountedPriceNumeric`, and a second value aggregation on `inStock`. */
  nested?: NestedAggregation;
}

export interface RangeBucket {
  /** Inclusive lower bound of the range. Required if `to` is not provided. */
  from?: number | null;
  /** Exclusive upper bound of the range. Required if `from` is not provided. */
  to?: number | null;
}

export enum SortType {
  /** Number of matches in the results. */
  COUNT = 'COUNT',
  /** Alphabetically by the field value. */
  VALUE = 'VALUE',
}

/** @enumType */
export type SortTypeWithLiterals = SortType | 'COUNT' | 'VALUE';

export enum SortDirection {
  /** Descending order. */
  DESC = 'DESC',
  /** Ascending order. */
  ASC = 'ASC',
}

/** @enumType */
export type SortDirectionWithLiterals = SortDirection | 'DESC' | 'ASC';

export enum MissingValues {
  /** Exclude missing values from the aggregation results. */
  EXCLUDE = 'EXCLUDE',
  /** Include missing values in the aggregation results. */
  INCLUDE = 'INCLUDE',
}

/** @enumType */
export type MissingValuesWithLiterals = MissingValues | 'EXCLUDE' | 'INCLUDE';

export interface IncludeMissingValuesOptions {
  /**
   * Specify a custom name for the bucket containing the missing values. Defaults are `"N/A"` for strings, `0` for integers, and `false` for booleans.
   * @maxLength 20
   */
  addToBucket?: string;
}

export enum ScalarType {
  /** Total number of distinct values. */
  COUNT_DISTINCT = 'COUNT_DISTINCT',
  /** Minimum value. */
  MIN = 'MIN',
  /** Maximum value. */
  MAX = 'MAX',
}

/** @enumType */
export type ScalarTypeWithLiterals =
  | ScalarType
  | 'COUNT_DISTINCT'
  | 'MIN'
  | 'MAX';

export interface ValueAggregation extends ValueAggregationOptionsOneOf {
  /** Options for including missing values in results. */
  includeOptions?: IncludeMissingValuesOptions;
  /** Sort type. */
  sortType?: SortTypeWithLiterals;
  /** Sort direction. */
  sortDirection?: SortDirectionWithLiterals;
  /**
   * Number of aggregation results to return.
   * Min: `1`
   * Max: `250`
   * Default: `10`
   */
  limit?: number | null;
  /**
   * Whether to include or exclude missing values in the aggregation results.
   * Default: `EXCLUDE`.
   */
  missingValues?: MissingValuesWithLiterals;
}

/** @oneof */
export interface ValueAggregationOptionsOneOf {
  /** Options for including missing values in results. */
  includeOptions?: IncludeMissingValuesOptions;
}

export enum NestedAggregationType {
  /** Calculates the distribution of a specific field's values within a dataset, providing insights into the overall distribution and key statistics of those values. */
  VALUE = 'VALUE',
  /** Calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. */
  RANGE = 'RANGE',
  /** Calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`. */
  SCALAR = 'SCALAR',
  /** Calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.). */
  DATE_HISTOGRAM = 'DATE_HISTOGRAM',
}

/** @enumType */
export type NestedAggregationTypeWithLiterals =
  | NestedAggregationType
  | 'VALUE'
  | 'RANGE'
  | 'SCALAR'
  | 'DATE_HISTOGRAM';

export interface RangeAggregation {
  /**
   * List of range buckets, where during aggregation each entity will be placed in the first bucket its value falls into, based on the provided range bounds.
   * @maxSize 50
   */
  buckets?: RangeBucket[];
}

export interface ScalarAggregation {
  /** Operator type for the scalar aggregation. */
  type?: ScalarTypeWithLiterals;
}

export interface DateHistogramAggregation {
  /** Interval for date histogram aggregation. */
  interval?: IntervalWithLiterals;
}

export enum Interval {
  /** Yearly interval */
  YEAR = 'YEAR',
  /** Monthly interval */
  MONTH = 'MONTH',
  /** Weekly interval */
  WEEK = 'WEEK',
  /** Daily interval */
  DAY = 'DAY',
  /** Hourly interval */
  HOUR = 'HOUR',
  /** Minute interval */
  MINUTE = 'MINUTE',
  /** Second interval */
  SECOND = 'SECOND',
}

/** @enumType */
export type IntervalWithLiterals =
  | Interval
  | 'YEAR'
  | 'MONTH'
  | 'WEEK'
  | 'DAY'
  | 'HOUR'
  | 'MINUTE'
  | 'SECOND';

export interface NestedAggregationItem extends NestedAggregationItemKindOneOf {
  /** A value aggregation calculates the distribution of a specific field's values within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number (count) of orders for each order status. */
  value?: ValueAggregation;
  /** A range aggregation calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. For example, use a range aggregation to compare the number of reservations made for parties of 4 or less to the number of reservations made for parties with 5 or more. If ranges overlap, a record that fits more than one range will only be counted in the first range that matches the criteria. */
  range?: RangeAggregation;
  /** A scalar aggregation calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`. */
  scalar?: ScalarAggregation;
  /** A date histogram calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.). For example, use a date histogram to determine how many reservations have been made at a restaurant each week. If ranges overlap, a record that fits more than one range will only be counted in the first range that matches the criteria. */
  dateHistogram?: DateHistogramAggregation;
  /**
   * Unique, caller-defined aggregation name, returned in `aggregations.results`.
   * @maxLength 100
   */
  name?: string | null;
  /** Type of aggregation to perform. The matching aggregation field must be passed. */
  type?: NestedAggregationTypeWithLiterals;
  /**
   * Field to aggregate by. Use dot notation to specify a JSON path. For example, `order.address.streetName`.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface NestedAggregationItemKindOneOf {
  /** A value aggregation calculates the distribution of a specific field's values within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number (count) of orders for each order status. */
  value?: ValueAggregation;
  /** A range aggregation calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. For example, use a range aggregation to compare the number of reservations made for parties of 4 or less to the number of reservations made for parties with 5 or more. If ranges overlap, a record that fits more than one range will only be counted in the first range that matches the criteria. */
  range?: RangeAggregation;
  /** A scalar aggregation calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`. */
  scalar?: ScalarAggregation;
  /** A date histogram calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.). For example, use a date histogram to determine how many reservations have been made at a restaurant each week. If ranges overlap, a record that fits more than one range will only be counted in the first range that matches the criteria. */
  dateHistogram?: DateHistogramAggregation;
}

export enum AggregationType {
  /** Calculates the distribution of a specific field's values within a dataset, providing insights into the overall distribution and key statistics of those values. */
  VALUE = 'VALUE',
  /** Calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. */
  RANGE = 'RANGE',
  /** Calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`. */
  SCALAR = 'SCALAR',
  /** Calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.). */
  DATE_HISTOGRAM = 'DATE_HISTOGRAM',
  /** Flattened list of aggregations, where each aggregation is nested within previous one. */
  NESTED = 'NESTED',
}

/** @enumType */
export type AggregationTypeWithLiterals =
  | AggregationType
  | 'VALUE'
  | 'RANGE'
  | 'SCALAR'
  | 'DATE_HISTOGRAM'
  | 'NESTED';

/** Nested aggregation expressed through a list of aggregation where each next aggregation is nested within previous one. */
export interface NestedAggregation {
  /**
   * Flattened list of aggregations, where each next aggregation is nested within previous one.
   * @minSize 2
   * @maxSize 3
   */
  nestedAggregations?: NestedAggregationItem[];
}

export interface GroupByAggregation extends GroupByAggregationKindOneOf {
  /** Value aggregation configuration. */
  value?: ValueAggregation;
  /**
   * Unique, caller-defined aggregation name, returned in `aggregations.results`.
   * @maxLength 100
   */
  name?: string | null;
  /**
   * Field to aggregate by.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface GroupByAggregationKindOneOf {
  /** Value aggregation configuration. */
  value?: ValueAggregation;
}

export interface SearchInventoryItemsResponse {
  /**
   * List of inventory items.
   * @minSize 1
   * @maxSize 1000
   */
  inventoryItems?: InventoryItem[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
  /** Aggregation data. */
  aggregationData?: AggregationData;
}

export interface CursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface AggregationData {
  /**
   * List of the aggregated data results.
   * @maxSize 10000
   */
  results?: AggregationResults[];
}

export interface ValueAggregationResult {
  /**
   * Value of the field.
   * @maxLength 100
   */
  value?: string;
  /** Number of entities with this value. */
  count?: number;
}

export interface RangeAggregationResult {
  /** Inclusive lower bound of the range. */
  from?: number | null;
  /** Exclusive upper bound of the range. */
  to?: number | null;
  /** Total number of entities in this range. */
  count?: number;
}

export interface NestedAggregationResults
  extends NestedAggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Range aggregation results. */
  ranges?: RangeResults;
  /** Scalar aggregation results. */
  scalar?: AggregationResultsScalarResult;
  /**
   * Unique, caller-defined aggregation name, identifiable by the requested aggregation `name`.
   * @maxLength 100
   */
  name?: string;
  /** Aggregation type. */
  type?: AggregationTypeWithLiterals;
  /**
   * Field which the data was aggregated by.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface NestedAggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Range aggregation results. */
  ranges?: RangeResults;
  /** Scalar aggregation results. */
  scalar?: AggregationResultsScalarResult;
}

export interface ValueResults {
  /**
   * List of value aggregations.
   * @maxSize 250
   */
  results?: ValueAggregationResult[];
}

export interface RangeResults {
  /**
   * List of ranges returned in same order as requested.
   * @maxSize 50
   */
  results?: RangeAggregationResult[];
}

export interface AggregationResultsScalarResult {
  /** Type of scalar aggregation. */
  type?: ScalarTypeWithLiterals;
  /** Value of the scalar aggregation. */
  value?: number;
}

export interface NestedValueAggregationResult {
  /**
   * Value of the field.
   * @maxLength 1000
   */
  value?: string;
  /** Nested aggregations. */
  nestedResults?: NestedAggregationResults;
}

export interface ValueResult {
  /**
   * Value of the field.
   * @maxLength 1000
   */
  value?: string;
  /** Number of entities with this value. */
  count?: number | null;
}

export interface RangeResult {
  /** Inclusive lower bound of the range. */
  from?: number | null;
  /** Exclusive upper bound of the range. */
  to?: number | null;
  /** Total number of entities in this range. */
  count?: number | null;
}

export interface ScalarResult {
  /** Value of the scalar aggregation. */
  value?: number;
}

export interface NestedResultValue extends NestedResultValueResultOneOf {
  /** Value aggregation result. */
  value?: ValueResult;
  /** Range aggregation result. */
  range?: RangeResult;
  /** Scalar aggregation result. */
  scalar?: ScalarResult;
  /** Date histogram aggregation result. */
  dateHistogram?: ValueResult;
}

/** @oneof */
export interface NestedResultValueResultOneOf {
  /** Value aggregation result. */
  value?: ValueResult;
  /** Range aggregation result. */
  range?: RangeResult;
  /** Scalar aggregation result. */
  scalar?: ScalarResult;
  /** Date histogram aggregation result. */
  dateHistogram?: ValueResult;
}

export interface Results {
  /** List of nested aggregations. */
  results?: Record<string, NestedResultValue>;
}

export interface DateHistogramResult {
  /**
   * Date in ISO 8601 format.
   * @maxLength 100
   */
  value?: string;
  /** Count of documents in the bucket. */
  count?: number;
}

export interface GroupByValueResults {
  /**
   * List of value aggregations.
   * @maxSize 1000
   */
  results?: NestedValueAggregationResult[];
}

export interface DateHistogramResults {
  /**
   * List of date histogram aggregations.
   * @maxSize 200
   */
  results?: DateHistogramResult[];
}

/**
 * Results of `NESTED` aggregation type in a flattened form.
 * Aggregations in resulting array are keyed by requested aggregation `name`.
 */
export interface NestedResults {
  /**
   * List of nested aggregations.
   * @maxSize 1000
   */
  results?: Results[];
}

export interface AggregationResults extends AggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Range aggregation results. */
  ranges?: RangeResults;
  /** Scalar aggregation results. */
  scalar?: AggregationResultsScalarResult;
  /** Group by value aggregation results. */
  groupedByValue?: GroupByValueResults;
  /** Date histogram aggregation results. */
  dateHistogram?: DateHistogramResults;
  /** Nested aggregation results. */
  nested?: NestedResults;
  /**
   * Aggregation name, returned in `aggregations.results.name`.
   * @maxLength 100
   */
  name?: string;
  /** Aggregation type. Must align with the corresponding aggregation field. */
  type?: AggregationTypeWithLiterals;
  /**
   * Field to aggregate by. Use dot notation to specify a JSON path. For example, `order.address.streetName`.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface AggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Range aggregation results. */
  ranges?: RangeResults;
  /** Scalar aggregation results. */
  scalar?: AggregationResultsScalarResult;
  /** Group by value aggregation results. */
  groupedByValue?: GroupByValueResults;
  /** Date histogram aggregation results. */
  dateHistogram?: DateHistogramResults;
  /** Nested aggregation results. */
  nested?: NestedResults;
}

export interface SearchInventoryItemsWithOffsetRequest {
  /** WQL expression. */
  search?: OffsetSearch;
}

export interface OffsetSearch extends OffsetSearchPagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#sorting).
   * @maxSize 10
   */
  sort?: Sorting[];
  /**
   * Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned.
   * @maxLength 200
   * @maxSize 20
   */
  fields?: string[];
  /**
   * Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned.
   * @maxLength 100
   * @maxSize 20
   */
  fieldsets?: string[];
  /**
   * Logical groupings of data into facets, with summaries for each facet. For example, use aggregations to allow site visitors to narrow down their search results by selecting specific categories.
   * @maxSize 10
   */
  aggregations?: Aggregation[];
  /** Free text to match in searchable fields. */
  search?: SearchDetails;
  /**
   * Time zone to adjust date-time-based filters and aggregations, in ISO 8601 (including offsets) or IANA time zone database (including time zone IDs) format.
   * Applies to all relevant filters and aggregations, unless overridden by providing timestamps including time zone. For example, "2023-12-20T10:52:34.795Z".
   * @maxLength 50
   */
  timeZone?: string | null;
}

/** @oneof */
export interface OffsetSearchPagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
}

export interface SearchInventoryItemsWithOffsetResponse {
  /** InventoryItems which satisfy the provided query. */
  inventoryItems?: InventoryItem[];
  /** Paging metadata. Contains cursor which can be used in next query. */
  pagingMetadata?: PagingMetadata;
}

export interface PagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
}

export interface BulkDecrementInventoryItemsRequest {
  /**
   * Inventory item IDs and decrement data.
   * @minSize 1
   * @maxSize 300
   */
  decrementData: DecrementDataById[];
  /**
   * Whether inventory is restricted from going below zero.
   *
   * Default: `true` - inventory does not go below zero.
   */
  restrictInventory?: boolean | null;
  /**
   * Whether to return the full inventory item entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
  /** Reason for decrement inventory action. */
  reason?: ReasonTypeWithLiterals;
}

export interface DecrementDataById {
  /**
   * Inventory item ID.
   * @format GUID
   */
  inventoryItemId?: string;
  /**
   * Amount to decrement by.
   * @min 1
   * @max 99999
   */
  decrementBy?: number;
  /**
   * Whether the request to decrement the inventory item's quantity was made as part of a purchase that includes preorder items.
   *
   * + If `true` and the item is available for preorder in the default location, negative inventory quantity is allowed.
   * + If `false` and the item is not available for preorder, negative inventory is not allowed.
   */
  preorderRequest?: boolean;
}

export interface BulkDecrementInventoryItemsResponse {
  /**
   * Inventory items updated by bulk action.
   * @minSize 1
   * @maxSize 300
   */
  results?: V3BulkInventoryItemResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkIncrementInventoryItemsRequest {
  /**
   * Inventory item IDs and increment data.
   * @minSize 1
   * @maxSize 300
   */
  incrementData: IncrementDataById[];
  /**
   * Whether to return the full inventory item entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
  /** Reason for increment inventory action. */
  reason?: ReasonTypeWithLiterals;
}

export interface IncrementDataById {
  /**
   * Inventory item ID.
   * @format GUID
   */
  inventoryItemId?: string;
  /**
   * Amount to increment by.
   * @min 1
   * @max 99999
   */
  incrementBy?: number;
}

export interface BulkIncrementInventoryItemsResponse {
  /**
   * Inventory items updated by bulk action.
   * @minSize 1
   * @maxSize 300
   */
  results?: V3BulkInventoryItemResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkDecrementInventoryItemsByVariantAndLocationRequest {
  /**
   * Variant and location IDs, as well as decrement data.
   * @minSize 1
   * @maxSize 300
   */
  decrementData: DecrementDataByVariantAndLocation[];
  /**
   * Whether to allow negative inventory following this decrement action.
   *
   * Default: `false` (negative inventory is not allowed)
   */
  restrictInventory?: boolean | null;
  /**
   * Whether to return the full inventory item entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
  /** Reason for decrement inventory action. */
  reason?: ReasonTypeWithLiterals;
}

export interface DecrementDataByVariantAndLocation {
  /**
   * Variant ID.
   * @format GUID
   */
  variantId?: string;
  /**
   * Location ID.
   * @format GUID
   */
  locationId?: string | null;
  /**
   * Amount to decrement by.
   * @min 1
   * @max 99999
   */
  decrementBy?: number;
  /**
   * Whether the request to decrement the inventory item's quantity was made as part of a purchase that includes preorder items.
   *
   * + If `true` and the item is available for preorder in the default location, negative inventory quantity is allowed.
   * + If `false` and the item is not available for preorder, negative inventory is not allowed.
   */
  preorderRequest?: boolean;
  /**
   * Product ID.
   *
   * Optional. Use together with `variantId` and `locationId` to uniquely identify the inventory item.
   * @internal
   * @minLength 1
   * @maxLength 36
   */
  productId?: string | null;
}

export interface BulkDecrementInventoryItemsByVariantAndLocationResponse {
  /**
   * Inventory items updated by bulk action.
   * @minSize 1
   * @maxSize 300
   */
  results?: V3BulkInventoryItemResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkIncrementInventoryItemsByVariantAndLocationRequest {
  /**
   * Variant and location IDs, as well as increment data.
   * @minSize 1
   * @maxSize 300
   */
  incrementData: IncrementDataByVariantAndLocation[];
  /**
   * Whether to return the full inventory item entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
  /** Reason for increment inventory action. */
  reason?: ReasonTypeWithLiterals;
}

export interface IncrementDataByVariantAndLocation {
  /**
   * Variant ID.
   * @format GUID
   */
  variantId?: string;
  /**
   * Location ID.
   * @format GUID
   */
  locationId?: string | null;
  /**
   * Amount to increment by.
   * @min 1
   * @max 99999
   */
  incrementBy?: number;
  /**
   * Product ID.
   *
   * Optional. Use together with `variantId` and `locationId` to uniquely identify the inventory item.
   * @internal
   * @minLength 1
   * @maxLength 36
   */
  productId?: string | null;
}

export interface BulkIncrementInventoryItemsByVariantAndLocationResponse {
  /**
   * Inventory items updated by bulk action.
   * @minSize 1
   * @maxSize 300
   */
  results?: V3BulkInventoryItemResult[];
  /** Bulk action metadata details as: totalSuccess and totalFailure. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

export interface RecloneSiteRequest {
  /** @format GUID */
  cloneFrom?: string;
}

export interface RecloneSiteResponse {}

export interface BulkSetOrAdjustInventoryItemsByFilterRequest {
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter: Record<string, any> | null;
  /** Action to adjust the inventory by. (e.g increment or decrement) */
  adjustedInventoryAction: AdjustInventoryAction;
  /** Free text to match in searchable fields */
  search?: SearchDetails;
}

export interface AdjustInventoryAction
  extends AdjustInventoryActionActionOneOf {
  /**
   * Amount to increment by.
   * @min 1
   * @max 99999
   */
  incrementBy?: number;
  /**
   * Amount to decrement by.
   * Quantity can be reduced until it reaches zero but not below. Attempting to further decrease an already negative inventory will fail.
   * @min 1
   * @max 99999
   */
  decrementBy?: number;
}

/** @oneof */
export interface AdjustInventoryActionActionOneOf {
  /**
   * Amount to increment by.
   * @min 1
   * @max 99999
   */
  incrementBy?: number;
  /**
   * Amount to decrement by.
   * Quantity can be reduced until it reaches zero but not below. Attempting to further decrease an already negative inventory will fail.
   * @min 1
   * @max 99999
   */
  decrementBy?: number;
}

export interface BulkSetOrAdjustInventoryItemsByFilterResponse {
  /**
   * Job ID.
   *
   * Pass this ID to [Get Async Job](https://dev.wix.com/docs/api-reference/business-management/async-job/introduction) to retrieve job details and metadata.
   * @format GUID
   */
  jobId?: string;
}

export interface BulkAdjustInventoryItemsByFilterRequest {
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter: Record<string, any> | null;
  /** Action to adjust the inventory by (e.g. increment or decrement). */
  adjustment: AdjustInventoryAction;
  /** Free text to match in searchable fields. */
  search?: SearchDetails;
  /** If set to `true`, will set track method to track quantity. Default: `false`. */
  trackQuantity?: boolean;
}

export interface BulkAdjustInventoryItemsByFilterResponse {
  /**
   * Job ID.
   *
   * Pass this ID to [Get Async Job](https://dev.wix.com/docs/api-reference/business-management/async-job/introduction) to retrieve job details and metadata.
   * @format GUID
   */
  jobId?: string;
}

export interface BulkCreateInventoryItemsByVariantFilterRequest {
  /**
   * Filter for variants. See [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language)
   * for WQL syntax and [Search Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/product-variants-reader/search-variants)
   * for available filter fields.
   */
  filter: Record<string, any> | null;
  /**
   * Free text to match in searchable fields.
   * When both `filter` and `search` are provided, results must satisfy both conditions (AND logic).
   * Use `filter` for precise field-based criteria and `search` for broad text matching.
   */
  search?: SearchDetails;
  /**
   * Target location ID where inventory items will be created.
   * @format GUID
   */
  locationId: string;
  /** Inventory data for created inventory items. */
  inventoryCreationData?: InventoryCreationData;
}

export interface InventoryCreationData {
  /**
   * Default quantity for created inventory items.
   * Items will be created with quantity tracking enabled and this initial quantity.
   * @max 99999
   */
  quantity?: number;
}

export interface BulkCreateInventoryItemsByVariantFilterResponse {
  /**
   * Job ID.
   *
   * Pass this ID to [Get Async Job](https://dev.wix.com/docs/api-reference/business-management/async-job/introduction) to retrieve job details and metadata.
   * @format GUID
   */
  jobId?: string;
}

/**
 * Published on Greyhound topic "catalog-rollback-requested" after the site is handled by the V3 population rollback.
 * Each owning service reacts to this rollback request according to its own policy.
 */
export interface CatalogRollbackRequested {
  /**
   * The MetaSite ID of the store whose catalog migration rollback was requested.
   * @format GUID
   */
  metaSiteId?: string;
  /** Whether owner services should clean V3 catalog data as a reaction to this rollback request. */
  cleanData?: boolean;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type CreateInventoryItemApplicationErrors =
  | {
      code?: 'ITEM_ALREADY_EXISTS';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PRE_ORDER_VALIDATION';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type CreateInventoryItemValidationErrors =
  | {
      ruleName?: 'REQUESTED_QUANTITY_MUST_BE_NON_NEGATIVE';
    }
  | {
      ruleName?: 'PREORDER_LIMIT_NOT_SUPPORTED_FOR_UNTRACKED_INVENTORY';
    }
  | {
      ruleName?: 'DUPLICATE_BULK_UPSERT_KEY';
    };
/** @docsIgnore */
export type BulkCreateInventoryItemsApplicationErrors = {
  code?: 'PRE_ORDER_VALIDATION';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type BulkCreateInventoryItemsValidationErrors =
  | {
      ruleName?: 'REQUESTED_QUANTITY_MUST_BE_NON_NEGATIVE';
    }
  | {
      ruleName?: 'PREORDER_LIMIT_NOT_SUPPORTED_FOR_UNTRACKED_INVENTORY';
    };
/** @docsIgnore */
export type UpdateInventoryItemApplicationErrors =
  | {
      code?: 'REQUESTED_QUANTITY_MUST_BE_NON_NEGATIVE_DECREASING';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PRE_ORDER_VALIDATION';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type BulkUpdateInventoryItemsApplicationErrors =
  | {
      code?: 'REQUESTED_QUANTITY_MUST_BE_NON_NEGATIVE_DECREASING';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PRE_ORDER_VALIDATION';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type BulkUpsertInventoryItemsApplicationErrors =
  | {
      code?: 'INVALID_TARGET_QUANTITY';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PRE_ORDER_VALIDATION';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type BulkUpdateInventoryItemsByFilterApplicationErrors =
  | {
      code?: 'REQUESTED_QUANTITY_MUST_BE_NON_NEGATIVE_DECREASING';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'PRE_ORDER_VALIDATION';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type BulkDecrementInventoryItemsApplicationErrors =
  | {
      code?: 'INVENTORY_QUANTITY_NOT_TRACKED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INSUFFICIENT_INVENTORY';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'MIN_QUANTITY_LIMIT_REACHED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type BulkIncrementInventoryItemsApplicationErrors =
  | {
      code?: 'INVENTORY_QUANTITY_NOT_TRACKED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'MAX_QUANTITY_LIMIT_REACHED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type BulkDecrementInventoryItemsByVariantAndLocationApplicationErrors =
  | {
      code?: 'INVENTORY_QUANTITY_NOT_TRACKED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INSUFFICIENT_INVENTORY';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'MIN_QUANTITY_LIMIT_REACHED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type BulkIncrementInventoryItemsByVariantAndLocationApplicationErrors =
  | {
      code?: 'INVENTORY_QUANTITY_NOT_TRACKED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'MAX_QUANTITY_LIMIT_REACHED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type BulkSetOrAdjustInventoryItemsByFilterApplicationErrors = {
  code?: 'INVENTORY_QUANTITY_NOT_TRACKED';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type BulkAdjustInventoryItemsByFilterApplicationErrors =
  | {
      code?: 'INVENTORY_QUANTITY_NOT_TRACKED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INSUFFICIENT_INVENTORY';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'MIN_QUANTITY_LIMIT_REACHED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'MAX_QUANTITY_LIMIT_REACHED';
      description?: string;
      data?: Record<string, any>;
    };

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface InventoryItemCreatedEnvelope {
  entity: InventoryItem;
  metadata: EventMetadata;
}

/** @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Read inventory in v3 catalog
 * @permissionScopeId SCOPE.STORES.INVENTORY_ITEM_READ
 * @permissionScope Read v3 catalog
 * @permissionScopeId SCOPE.STORES.CATALOG_READ
 * @permissionScope Manage Products
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-PRODUCTS
 * @permissionScope Read Stores - all read permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.READ-STORES
 * @permissionScope Read inventory in v3 catalog (PII)
 * @permissionScopeId SCOPE.STORES.INVENTORY_ITEM_READ_LIMITED
 * @permissionScope Read Products
 * @permissionScopeId SCOPE.DC-STORES.READ-PRODUCTS
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage v3 catalog
 * @permissionScopeId SCOPE.STORES.CATALOG_WRITE
 * @permissionScope Read v3 catalog (PII)
 * @permissionScopeId SCOPE.STORES.CATALOG_READ_LIMITED
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @permissionId WIX_STORES.INVENTORY_READ
 * @webhook
 * @eventType wix.stores.catalog.v3.inventory_item_created
 * @slug created
 */
export declare function onInventoryItemCreated(
  handler: (event: InventoryItemCreatedEnvelope) => void | Promise<void>
): void;

export interface InventoryItemDeletedEnvelope {
  entity: InventoryItem;
  metadata: EventMetadata;
}

/** @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Read inventory in v3 catalog
 * @permissionScopeId SCOPE.STORES.INVENTORY_ITEM_READ
 * @permissionScope Read v3 catalog
 * @permissionScopeId SCOPE.STORES.CATALOG_READ
 * @permissionScope Manage Products
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-PRODUCTS
 * @permissionScope Read Stores - all read permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.READ-STORES
 * @permissionScope Read inventory in v3 catalog (PII)
 * @permissionScopeId SCOPE.STORES.INVENTORY_ITEM_READ_LIMITED
 * @permissionScope Read Products
 * @permissionScopeId SCOPE.DC-STORES.READ-PRODUCTS
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage v3 catalog
 * @permissionScopeId SCOPE.STORES.CATALOG_WRITE
 * @permissionScope Read v3 catalog (PII)
 * @permissionScopeId SCOPE.STORES.CATALOG_READ_LIMITED
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @permissionId WIX_STORES.INVENTORY_READ
 * @webhook
 * @eventType wix.stores.catalog.v3.inventory_item_deleted
 * @slug deleted
 */
export declare function onInventoryItemDeleted(
  handler: (event: InventoryItemDeletedEnvelope) => void | Promise<void>
): void;

export interface InventoryItemStockStatusUpdatedEnvelope {
  data: InventoryItemStockStatusUpdatedEvent;
  metadata: EventMetadata;
}

/**
 * Triggered when an inventory item's stock is updated.
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Read inventory in v3 catalog
 * @permissionScopeId SCOPE.STORES.INVENTORY_ITEM_READ
 * @permissionScope Read v3 catalog
 * @permissionScopeId SCOPE.STORES.CATALOG_READ
 * @permissionScope Manage Products
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-PRODUCTS
 * @permissionScope Read Stores - all read permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.READ-STORES
 * @permissionScope Read inventory in v3 catalog (PII)
 * @permissionScopeId SCOPE.STORES.INVENTORY_ITEM_READ_LIMITED
 * @permissionScope Read Products
 * @permissionScopeId SCOPE.DC-STORES.READ-PRODUCTS
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage v3 catalog
 * @permissionScopeId SCOPE.STORES.CATALOG_WRITE
 * @permissionScope Read v3 catalog (PII)
 * @permissionScopeId SCOPE.STORES.CATALOG_READ_LIMITED
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @permissionId WIX_STORES.INVENTORY_READ
 * @webhook
 * @eventType wix.stores.catalog.v3.inventory_item_stock_status_updated
 * @serviceIdentifier com.wix.stores.catalog.inventory.api.v3.InventoryService
 * @slug stock_status_updated
 */
export declare function onInventoryItemStockStatusUpdated(
  handler: (
    event: InventoryItemStockStatusUpdatedEnvelope
  ) => void | Promise<void>
): void;

export interface InventoryItemUpdatedEnvelope {
  entity: InventoryItem;
  metadata: EventMetadata;
  /** @hidden */
  modifiedFields: Record<string, any>;
}

/** @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Read inventory in v3 catalog
 * @permissionScopeId SCOPE.STORES.INVENTORY_ITEM_READ
 * @permissionScope Read v3 catalog
 * @permissionScopeId SCOPE.STORES.CATALOG_READ
 * @permissionScope Manage Products
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-PRODUCTS
 * @permissionScope Read Stores - all read permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.READ-STORES
 * @permissionScope Read inventory in v3 catalog (PII)
 * @permissionScopeId SCOPE.STORES.INVENTORY_ITEM_READ_LIMITED
 * @permissionScope Read Products
 * @permissionScopeId SCOPE.DC-STORES.READ-PRODUCTS
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage v3 catalog
 * @permissionScopeId SCOPE.STORES.CATALOG_WRITE
 * @permissionScope Read v3 catalog (PII)
 * @permissionScopeId SCOPE.STORES.CATALOG_READ_LIMITED
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @permissionId WIX_STORES.INVENTORY_READ
 * @webhook
 * @eventType wix.stores.catalog.v3.inventory_item_updated
 * @slug updated
 */
export declare function onInventoryItemUpdated(
  handler: (event: InventoryItemUpdatedEnvelope) => void | Promise<void>
): void;

export interface InventoryItemUpdatedWithReasonEnvelope {
  data: InventoryItemUpdatedWithReason;
  metadata: EventMetadata;
}

/**
 * Triggered when an inventory item is updated.
 * @permissionScope Manage Stores - all permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.MANAGE-STORES
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Read inventory in v3 catalog
 * @permissionScopeId SCOPE.STORES.INVENTORY_ITEM_READ
 * @permissionScope Read v3 catalog
 * @permissionScopeId SCOPE.STORES.CATALOG_READ
 * @permissionScope Manage Products
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-PRODUCTS
 * @permissionScope Read Stores - all read permissions
 * @permissionScopeId SCOPE.DC-STORES-MEGA.READ-STORES
 * @permissionScope Read inventory in v3 catalog (PII)
 * @permissionScopeId SCOPE.STORES.INVENTORY_ITEM_READ_LIMITED
 * @permissionScope Read Products
 * @permissionScopeId SCOPE.DC-STORES.READ-PRODUCTS
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Manage v3 catalog
 * @permissionScopeId SCOPE.STORES.CATALOG_WRITE
 * @permissionScope Read v3 catalog (PII)
 * @permissionScopeId SCOPE.STORES.CATALOG_READ_LIMITED
 * @permissionScope Manage Orders
 * @permissionScopeId SCOPE.DC-STORES.MANAGE-ORDERS
 * @permissionId WIX_STORES.INVENTORY_READ
 * @webhook
 * @eventType wix.stores.catalog.v3.inventory_item_updated_with_reason
 * @serviceIdentifier com.wix.stores.catalog.inventory.api.v3.InventoryService
 * @slug updated_with_reason
 */
export declare function onInventoryItemUpdatedWithReason(
  handler: (
    event: InventoryItemUpdatedWithReasonEnvelope
  ) => void | Promise<void>
): void;

/**
 * Creates an inventory item.
 *
 * The combination of `variantId` and `locationId` must be unique.
 * @param inventoryItem - Inventory item to create.
 * @public
 * @requiredField inventoryItem
 * @requiredField inventoryItem.productId
 * @requiredField inventoryItem.trackingMethod
 * @requiredField inventoryItem.variantId
 * @permissionId WIX_STORES.INVENTORY_CREATE
 * @applicableIdentity APP
 * @returns Created inventory item.
 * @fqn com.wix.stores.catalog.inventory.api.v3.InventoryService.CreateInventoryItem
 */
export async function createInventoryItem(
  inventoryItem: NonNullablePaths<InventoryItem, `productId` | `variantId`, 2>
): Promise<
  NonNullablePaths<
    InventoryItem,
    | `inStock`
    | `quantity`
    | `variantId`
    | `productId`
    | `trackQuantity`
    | `availabilityStatus`
    | `product.directCategoryIds`,
    3
  > & {
    __applicationErrorsType?: CreateInventoryItemApplicationErrors;
    __validationErrorsType?: CreateInventoryItemValidationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    inventoryItem: inventoryItem,
  });

  const reqOpts =
    ambassadorWixStoresCatalogV3InventoryItem.createInventoryItem(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.inventoryItem!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { inventoryItem: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['inventoryItem']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Creates multiple inventory items.
 *
 * The combination of `variantId` and `locationId` must be unique for each inventory item.
 * @param inventoryItems - Inventory items to create.
 * @public
 * @requiredField inventoryItems
 * @requiredField inventoryItems.productId
 * @requiredField inventoryItems.trackingMethod
 * @requiredField inventoryItems.variantId
 * @permissionId WIX_STORES.INVENTORY_CREATE
 * @applicableIdentity APP
 * @fqn com.wix.stores.catalog.inventory.api.v3.InventoryService.BulkCreateInventoryItems
 */
export async function bulkCreateInventoryItems(
  inventoryItems: NonNullablePaths<
    InventoryItem,
    `productId` | `variantId`,
    2
  >[],
  options?: BulkCreateInventoryItemsOptions
): Promise<
  NonNullablePaths<
    BulkCreateInventoryItemsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.inStock`
    | `results.${number}.item.quantity`
    | `results.${number}.item.variantId`
    | `results.${number}.item.productId`
    | `results.${number}.item.trackQuantity`
    | `results.${number}.item.availabilityStatus`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  > & {
    __applicationErrorsType?: BulkCreateInventoryItemsApplicationErrors;
    __validationErrorsType?: BulkCreateInventoryItemsValidationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    inventoryItems: inventoryItems,
    returnEntity: options?.returnEntity,
  });

  const reqOpts =
    ambassadorWixStoresCatalogV3InventoryItem.bulkCreateInventoryItems(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          inventoryItems: '$[0]',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['inventoryItems', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkCreateInventoryItemsOptions {
  /**
   * Whether to return the full inventory item entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
}

/**
 * Retrieves an inventory item.
 * @param inventoryItemId - Inventory item ID.
 * @public
 * @requiredField inventoryItemId
 * @permissionId WIX_STORES.INVENTORY_READ
 * @applicableIdentity APP
 * @returns Inventory item.
 * @fqn com.wix.stores.catalog.inventory.api.v3.InventoryService.GetInventoryItem
 */
export async function getInventoryItem(
  inventoryItemId: string
): Promise<
  NonNullablePaths<
    InventoryItem,
    | `inStock`
    | `quantity`
    | `variantId`
    | `productId`
    | `trackQuantity`
    | `availabilityStatus`
    | `product.directCategoryIds`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    inventoryItemId: inventoryItemId,
  });

  const reqOpts =
    ambassadorWixStoresCatalogV3InventoryItem.getInventoryItem(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.inventoryItem!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { inventoryItemId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['inventoryItemId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates an inventory item.
 *
 *
 * Each time the inventory item is updated, `revision` increments by 1.
 * The current `revision` must be passed when updating the inventory item.
 * This ensures you're working with the latest inventory item and prevents unintended overwrites.
 * @param _id - Inventory item ID.
 * @public
 * @requiredField _id
 * @requiredField inventoryItem
 * @requiredField inventoryItem.revision
 * @permissionId WIX_STORES.INVENTORY_UPDATE
 * @applicableIdentity APP
 * @returns Updated inventory item.
 * @fqn com.wix.stores.catalog.inventory.api.v3.InventoryService.UpdateInventoryItem
 */
export async function updateInventoryItem(
  _id: string,
  inventoryItem: NonNullablePaths<UpdateInventoryItem, `revision`, 2>,
  options?: UpdateInventoryItemOptions
): Promise<
  NonNullablePaths<
    InventoryItem,
    | `inStock`
    | `quantity`
    | `variantId`
    | `productId`
    | `trackQuantity`
    | `availabilityStatus`
    | `product.directCategoryIds`,
    3
  > & {
    __applicationErrorsType?: UpdateInventoryItemApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    inventoryItem: { ...inventoryItem, id: _id },
    reason: options?.reason,
  });

  const reqOpts =
    ambassadorWixStoresCatalogV3InventoryItem.updateInventoryItem(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.inventoryItem!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { inventoryItem: '$[1]' },
        explicitPathsToArguments: {
          'inventoryItem.id': '$[0]',
          reason: '$[2].reason',
        },
        singleArgumentUnchanged: false,
      },
      ['_id', 'inventoryItem', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateInventoryItem {
  /**
   * Indicates that inventory is tracked by status rather than quantity.
   *
   * When set to `true`, the item is marked as available for sale without tracking exact quantities.
   * When set to `false`, the item is marked as out of stock.
   * This tracking method is useful for made-to-order products or items with unlimited inventory.
   *
   * When using this tracking method, `trackQuantity` is `false` and preorder limits aren't supported.
   */
  inStock?: boolean;
  /**
   * Indicates that inventory is tracked by quantity.
   *
   * Set this field to the number of items currently in stock.
   * This tracking method is useful when you need to know exactly how many items are available.
   *
   * When using this tracking method, `trackQuantity` is `true`.
   * Quantity can be negative when inventory is decremented for an order that has already been paid.
   * @min -99999
   * @max 99999
   */
  quantity?: number;
  /**
   * Inventory item ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the inventory item is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the inventory item.
   *
   * Ignored when creating an inventory item.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the inventory item was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the inventory item was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Variant ID.
   * @format GUID
   * @immutable
   */
  variantId?: string;
  /**
   * Stores location ID. If not specified when creating an inventory item, the store's default location is used.
   * @format GUID
   * @immutable
   */
  locationId?: string | null;
  /**
   * Product ID.
   * @minLength 1
   * @maxLength 36
   * @immutable
   */
  productId?: string;
  /**
   * Whether the quantity is being tracked.
   * @readonly
   */
  trackQuantity?: boolean;
  /**
   * Inventory item availability status.
   * @readonly
   */
  availabilityStatus?: AvailabilityStatusWithLiterals;
  /**
   * Item preorder info.
   *
   * Preorder settings are configured per inventory item, so each variant-location combination can have its own preorder configuration.
   *
   * > **Note:** The product entity's `inventory.preorderStatus` and `inventory.preorderAvailability` fields reflect only the default location's preorder state.
   */
  preorderInfo?: PreorderInfo;
  /**
   * Associated product and variant details.
   * @readonly
   */
  product?: Product;
  /**
   * Custom field data for the inventory item object.
   *
   * [Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.
   */
  extendedFields?: ExtendedFields;
}

export interface UpdateInventoryItemOptions {
  /** Reason for update. */
  reason?: ReasonTypeWithLiterals;
}

/**
 * Updates multiple inventory items.
 *
 * Each time an inventory item is updated, `revision` increments by 1.
 * The current `revision` must be passed when updating an inventory item.
 * This ensures you're working with the latest inventory item and prevents unintended overwrites.
 * @param inventoryItems - Inventory items to update.
 * @public
 * @requiredField inventoryItems
 * @requiredField inventoryItems.inventoryItem._id
 * @requiredField inventoryItems.inventoryItem.revision
 * @permissionId WIX_STORES.INVENTORY_UPDATE
 * @applicableIdentity APP
 * @fqn com.wix.stores.catalog.inventory.api.v3.InventoryService.BulkUpdateInventoryItems
 */
export async function bulkUpdateInventoryItems(
  inventoryItems: NonNullablePaths<
    MaskedInventoryItem,
    `inventoryItem._id` | `inventoryItem.revision`,
    3
  >[],
  options?: BulkUpdateInventoryItemsOptions
): Promise<
  NonNullablePaths<
    BulkUpdateInventoryItemsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.inStock`
    | `results.${number}.item.quantity`
    | `results.${number}.item.variantId`
    | `results.${number}.item.productId`
    | `results.${number}.item.trackQuantity`
    | `results.${number}.item.availabilityStatus`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  > & {
    __applicationErrorsType?: BulkUpdateInventoryItemsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    inventoryItems: inventoryItems,
    returnEntity: options?.returnEntity,
    reason: options?.reason,
  });

  const reqOpts =
    ambassadorWixStoresCatalogV3InventoryItem.bulkUpdateInventoryItems(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          inventoryItems: '$[0]',
          returnEntity: '$[1].returnEntity',
          reason: '$[1].reason',
        },
        singleArgumentUnchanged: false,
      },
      ['inventoryItems', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateInventoryItemsOptions {
  /**
   * Whether to return the full inventory item entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
  /** Reason for update. */
  reason?: ReasonTypeWithLiterals;
}

/**
 * Creates or updates multiple inventory items.
 *
 * The upsert key is the combination of `productId`, `variantId`, and `locationId`.
 * If `locationId` isn't provided, the site's default location is used.
 *
 * For newly created items, `field_mask` is ignored and all provided fields are applied.
 * For existing items, only the fields specified in `field_mask` are updated.
 * @param inventoryItems - Inventory items to create or update.
 * @internal
 * @documentationMaturity preview
 * @requiredField inventoryItems
 * @requiredField inventoryItems.inventoryItem
 * @requiredField inventoryItems.inventoryItem.productId
 * @requiredField inventoryItems.inventoryItem.variantId
 * @permissionId WIX_STORES.INVENTORY_UPSERT
 * @applicableIdentity APP
 * @fqn com.wix.stores.catalog.inventory.api.v3.InventoryService.BulkUpsertInventoryItems
 */
export async function bulkUpsertInventoryItems(
  inventoryItems: NonNullablePaths<
    MaskedInventoryItem,
    `inventoryItem` | `inventoryItem.productId` | `inventoryItem.variantId`,
    3
  >[],
  options?: BulkUpsertInventoryItemsOptions
): Promise<
  NonNullablePaths<
    BulkUpsertInventoryItemsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.inStock`
    | `results.${number}.item.quantity`
    | `results.${number}.item.variantId`
    | `results.${number}.item.productId`
    | `results.${number}.item.trackQuantity`
    | `results.${number}.item.availabilityStatus`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  > & {
    __applicationErrorsType?: BulkUpsertInventoryItemsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    inventoryItems: inventoryItems,
    returnEntity: options?.returnEntity,
  });

  const reqOpts =
    ambassadorWixStoresCatalogV3InventoryItem.bulkUpsertInventoryItems(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          inventoryItems: '$[0]',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['inventoryItems', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpsertInventoryItemsOptions {
  /**
   * Whether to return the full inventory item entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
}

/**
 * Updates multiple inventory items, given the provided filter.
 *
 * Each time an inventory item is updated, `revision` increments by 1.
 * The current `revision` must be passed when updating an inventory item.
 * This ensures you're working with the latest inventory item and prevents unintended overwrites.
 * @param filter - Filter object.
 * @public
 * @requiredField filter
 * @requiredField options.inventoryItem
 * @permissionId WIX_STORES.INVENTORY_UPDATE
 * @applicableIdentity APP
 * @fqn com.wix.stores.catalog.inventory.api.v3.InventoryService.BulkUpdateInventoryItemsByFilter
 */
export async function bulkUpdateInventoryItemsByFilter(
  filter: Record<string, any>,
  options?: NonNullablePaths<
    BulkUpdateInventoryItemsByFilterOptions,
    `inventoryItem`,
    2
  >
): Promise<
  NonNullablePaths<BulkUpdateInventoryItemsByFilterResponse, `jobId`, 2> & {
    __applicationErrorsType?: BulkUpdateInventoryItemsByFilterApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: filter,
    inventoryItem: options?.inventoryItem,
    search: options?.search,
  });

  const reqOpts =
    ambassadorWixStoresCatalogV3InventoryItem.bulkUpdateInventoryItemsByFilter(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          filter: '$[0]',
          inventoryItem: '$[1].inventoryItem',
          search: '$[1].search',
        },
        singleArgumentUnchanged: false,
      },
      ['filter', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateInventoryItemsByFilterOptions {
  /** Inventory item to update. */
  inventoryItem: InventoryItem;
  /** Free text to match in searchable fields. */
  search?: SearchDetails;
}

/**
 * Deletes an inventory item.
 * @param inventoryItemId - Inventory item ID.
 * @public
 * @requiredField inventoryItemId
 * @permissionId WIX_STORES.INVENTORY_DELETE
 * @applicableIdentity APP
 * @fqn com.wix.stores.catalog.inventory.api.v3.InventoryService.DeleteInventoryItem
 */
export async function deleteInventoryItem(
  inventoryItemId: string
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    inventoryItemId: inventoryItemId,
  });

  const reqOpts =
    ambassadorWixStoresCatalogV3InventoryItem.deleteInventoryItem(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { inventoryItemId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['inventoryItemId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Deletes multiple inventory items.
 * @param inventoryItemIds - IDs of inventory items to delete.
 * @public
 * @requiredField inventoryItemIds
 * @permissionId WIX_STORES.INVENTORY_DELETE
 * @applicableIdentity APP
 * @fqn com.wix.stores.catalog.inventory.api.v3.InventoryService.BulkDeleteInventoryItems
 */
export async function bulkDeleteInventoryItems(
  inventoryItemIds: string[]
): Promise<
  NonNullablePaths<
    BulkDeleteInventoryItemsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    inventoryItemIds: inventoryItemIds,
  });

  const reqOpts =
    ambassadorWixStoresCatalogV3InventoryItem.bulkDeleteInventoryItems(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { inventoryItemIds: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['inventoryItemIds']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Deletes multiple inventory items, given the provided filter.
 *
 * The operation runs asynchronously. Use the returned job ID to track progress.
 * @param filter - Filter object in the following format:
 * `"filter" : {
 * "fieldName1": "value1",
 * "fieldName2":{"$operator":"value2"}
 * }`
 * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
 * @internal
 * @documentationMaturity preview
 * @requiredField filter
 * @permissionId WIX_STORES.INVENTORY_DELETE
 * @applicableIdentity APP
 * @fqn com.wix.stores.catalog.inventory.api.v3.InventoryService.BulkDeleteInventoryItemsByFilter
 */
export async function bulkDeleteInventoryItemsByFilter(
  filter: Record<string, any>,
  options?: BulkDeleteInventoryItemsByFilterOptions
): Promise<
  NonNullablePaths<BulkDeleteInventoryItemsByFilterResponse, `jobId`, 2>
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: filter,
    search: options?.search,
  });

  const reqOpts =
    ambassadorWixStoresCatalogV3InventoryItem.bulkDeleteInventoryItemsByFilter(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { filter: '$[0]', search: '$[1].search' },
        singleArgumentUnchanged: false,
      },
      ['filter', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkDeleteInventoryItemsByFilterOptions {
  /** Free text to match in searchable fields. */
  search?: SearchDetails;
}

/**
 * Retrieves a list of up to 1,000 inventory items, given the provided filtering, sorting, and cursor paging.
 *
 * To learn about working with _Query_ endpoints, see
 * [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language),
 * and [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging).
 * Learn more about the differences between [_Query_ and _Search_](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-search-query-and-list-methods) methods.
 * @public
 * @permissionId WIX_STORES.INVENTORY_READ
 * @applicableIdentity APP
 * @fqn com.wix.stores.catalog.inventory.api.v3.InventoryService.QueryInventoryItems
 */
export function queryInventoryItems(): InventoryItemsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    InventoryItem,
    'CURSOR',
    QueryInventoryItemsRequest,
    QueryInventoryItemsResponse
  >({
    func: async (payload: QueryInventoryItemsRequest) => {
      const reqOpts =
        ambassadorWixStoresCatalogV3InventoryItem.queryInventoryItems(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryInventoryItemsRequest['query']) => {
      const args = [query, {}] as [QueryInventoryItemsRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryInventoryItemsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.inventoryItems,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface InventoryItemsQueryResult extends QueryCursorResult {
  items: InventoryItem[];
  query: InventoryItemsQueryBuilder;
  next: () => Promise<InventoryItemsQueryResult>;
  prev: () => Promise<InventoryItemsQueryResult>;
}

export interface InventoryItemsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName:
      | 'inStock'
      | 'quantity'
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'variantId'
      | 'locationId'
      | 'productId'
      | 'trackQuantity'
      | 'availabilityStatus'
      | 'preorderInfo.enabled'
      | 'product.name'
      | 'product.directCategoryIds'
      | 'product.variantName'
      | 'product.variantSku'
      | 'product.brand.id',
    value: any
  ) => InventoryItemsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName:
      | 'inStock'
      | 'quantity'
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'variantId'
      | 'locationId'
      | 'productId'
      | 'trackQuantity'
      | 'availabilityStatus'
      | 'preorderInfo.enabled'
      | 'product.name'
      | 'product.directCategoryIds'
      | 'product.variantName'
      | 'product.variantSku'
      | 'product.brand.id',
    value: any
  ) => InventoryItemsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName:
      | 'quantity'
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'variantId'
      | 'locationId'
      | 'productId'
      | 'product.name'
      | 'product.variantName'
      | 'product.variantSku'
      | 'product.brand.id',
    value: any
  ) => InventoryItemsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName:
      | 'quantity'
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'variantId'
      | 'locationId'
      | 'productId'
      | 'product.name'
      | 'product.variantName'
      | 'product.variantSku'
      | 'product.brand.id',
    value: any
  ) => InventoryItemsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName:
      | 'quantity'
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'variantId'
      | 'locationId'
      | 'productId'
      | 'product.name'
      | 'product.variantName'
      | 'product.variantSku'
      | 'product.brand.id',
    value: any
  ) => InventoryItemsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName:
      | 'quantity'
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'variantId'
      | 'locationId'
      | 'productId'
      | 'product.name'
      | 'product.variantName'
      | 'product.variantSku'
      | 'product.brand.id',
    value: any
  ) => InventoryItemsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   */
  startsWith: (
    propertyName:
      | '_id'
      | 'variantId'
      | 'locationId'
      | 'productId'
      | 'product.name'
      | 'product.variantName'
      | 'product.variantSku'
      | 'product.brand.id',
    value: string
  ) => InventoryItemsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasSome: (
    propertyName:
      | 'inStock'
      | 'quantity'
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'variantId'
      | 'locationId'
      | 'productId'
      | 'trackQuantity'
      | 'availabilityStatus'
      | 'preorderInfo.enabled'
      | 'product.name'
      | 'product.directCategoryIds'
      | 'product.variantName'
      | 'product.variantSku'
      | 'product.brand.id',
    value: any[]
  ) => InventoryItemsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasAll: (
    propertyName: 'product.directCategoryIds',
    value: any[]
  ) => InventoryItemsQueryBuilder;
  in: (
    propertyName:
      | 'inStock'
      | 'quantity'
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'variantId'
      | 'locationId'
      | 'productId'
      | 'trackQuantity'
      | 'availabilityStatus'
      | 'preorderInfo.enabled'
      | 'product.name'
      | 'product.directCategoryIds'
      | 'product.variantName'
      | 'product.variantSku'
      | 'product.brand.id',
    value: any
  ) => InventoryItemsQueryBuilder;
  exists: (
    propertyName:
      | 'inStock'
      | 'quantity'
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'variantId'
      | 'locationId'
      | 'productId'
      | 'trackQuantity'
      | 'availabilityStatus'
      | 'preorderInfo.enabled'
      | 'product.name'
      | 'product.directCategoryIds'
      | 'product.variantName'
      | 'product.variantSku'
      | 'product.brand.id',
    value: boolean
  ) => InventoryItemsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<
      | 'inStock'
      | 'quantity'
      | '_createdDate'
      | '_updatedDate'
      | 'productId'
      | 'trackQuantity'
      | 'availabilityStatus'
      | 'preorderInfo.enabled'
      | 'product.name'
      | 'product.directCategoryIds'
      | 'product.variantName'
      | 'product.variantSku'
      | 'product.brand.id'
    >
  ) => InventoryItemsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<
      | 'inStock'
      | 'quantity'
      | '_createdDate'
      | '_updatedDate'
      | 'productId'
      | 'trackQuantity'
      | 'availabilityStatus'
      | 'preorderInfo.enabled'
      | 'product.name'
      | 'product.directCategoryIds'
      | 'product.variantName'
      | 'product.variantSku'
      | 'product.brand.id'
    >
  ) => InventoryItemsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => InventoryItemsQueryBuilder;
  /** @param cursor - A pointer to specific record */
  skipTo: (cursor: string) => InventoryItemsQueryBuilder;
  find: () => Promise<InventoryItemsQueryResult>;
}

/**
 * @hidden
 * @fqn com.wix.stores.catalog.inventory.api.v3.InventoryService.QueryInventoryItems
 * @requiredField query
 */
export async function typedQueryInventoryItems(
  query: InventoryItemQuery
): Promise<
  NonNullablePaths<
    QueryInventoryItemsResponse,
    | `inventoryItems`
    | `inventoryItems.${number}.inStock`
    | `inventoryItems.${number}.quantity`
    | `inventoryItems.${number}.variantId`
    | `inventoryItems.${number}.productId`
    | `inventoryItems.${number}.trackQuantity`
    | `inventoryItems.${number}.availabilityStatus`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixStoresCatalogV3InventoryItem.queryInventoryItems(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface InventoryItemQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: ['_id', 'locationId', 'variantId'];
      operators: '*';
      sort: 'NONE';
    },
    {
      fields: ['product.directCategoryIds'];
      operators: ['$hasAll', '$hasSome'];
      sort: 'BOTH';
    },
    {
      fields: [
        '_createdDate',
        '_updatedDate',
        'availabilityStatus',
        'inStock',
        'preorderInfo.enabled',
        'product.brand._id',
        'product.name',
        'product.variantName',
        'product.variantSku',
        'productId',
        'quantity',
        'trackQuantity'
      ];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  InventoryItem,
  InventoryItemQuerySpec
>;
export type InventoryItemQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 1000 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.

  Learn more about [filtering](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#filters).  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object.

  Learn more about [sorting](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#sorting). 
  @maxSize: 10 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<
      InventoryItem,
      InventoryItemQuerySpec,
      InventoryItemQuery
    >(),
  },
  search: {
    ...createSearchUtils<
      InventoryItem,
      InventoryItemSearchSpec,
      InventoryItemSearch
    >(),
  },
};

/**
 * Retrieves a list of inventory items, given the provided filtering, sorting, and cursor paging.
 *
 *
 * Search Inventory Items runs with these defaults, which you can override:
 *
 * - `createdDate` is sorted in `DESC` order
 * - `cursorPaging.limit` is `100`
 *
 * To learn about working with _Search_ endpoints, see
 * [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language),
 * and [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging).
 *
 * Learn more about the differences between [_Query_ and _Search_](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-search-query-and-list-methods) methods.
 * @param search - Search options.
 * @public
 * @requiredField search
 * @permissionId WIX_STORES.INVENTORY_READ
 * @applicableIdentity APP
 * @fqn com.wix.stores.catalog.inventory.api.v3.InventoryService.SearchInventoryItems
 */
export async function searchInventoryItems(
  search: InventoryItemSearch
): Promise<
  NonNullablePaths<
    SearchInventoryItemsResponse,
    | `inventoryItems`
    | `inventoryItems.${number}.inStock`
    | `inventoryItems.${number}.quantity`
    | `inventoryItems.${number}.variantId`
    | `inventoryItems.${number}.productId`
    | `inventoryItems.${number}.trackQuantity`
    | `inventoryItems.${number}.availabilityStatus`
    | `aggregationData.results`
    | `aggregationData.results.${number}.scalar.type`
    | `aggregationData.results.${number}.scalar.value`
    | `aggregationData.results.${number}.name`
    | `aggregationData.results.${number}.type`
    | `aggregationData.results.${number}.fieldPath`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ search: search });

  const reqOpts =
    ambassadorWixStoresCatalogV3InventoryItem.searchInventoryItems(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { search: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['search']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface InventoryItemSearchSpec extends SearchSpec {
  searchable: ['product.name', 'product.variantName', 'product.variantSku'];
  aggregatable: [
    '_createdDate',
    '_id',
    '_updatedDate',
    'availabilityStatus',
    'inStock',
    'locationId',
    'preorderInfo.enabled',
    'product.brand._id',
    'product.directCategoryIds',
    'product.name',
    'product.variantName',
    'product.variantSku',
    'productId',
    'quantity',
    'trackQuantity',
    'variantId'
  ];
  paging: 'cursor';
  wql: [
    {
      operators: '*';
      fields: ['_id', 'locationId', 'variantId'];
      sort: 'NONE';
    },
    {
      operators: ['$hasAll', '$hasSome'];
      fields: ['product.directCategoryIds'];
      sort: 'BOTH';
    },
    {
      operators: '*';
      fields: [
        '_createdDate',
        '_updatedDate',
        'availabilityStatus',
        'inStock',
        'preorderInfo.enabled',
        'product.brand._id',
        'product.name',
        'product.variantName',
        'product.variantSku',
        'productId',
        'quantity',
        'trackQuantity'
      ];
      sort: 'BOTH';
    }
  ];
}

export type CommonSearchWithEntityContext = SearchSdkType<
  InventoryItem,
  InventoryItemSearchSpec
>;
export type InventoryItemSearch = {
  /** 
  Cursor paging options.

  Learn more about [cursor paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#cursor-paging).  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 1000 
  */
    limit?:
      | NonNullable<CommonSearchWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonSearchWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.

  Learn more about [filtering](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#filters).  
  */
  filter?: CommonSearchWithEntityContext['filter'] | null;
  /** 
  List of sort objects.

  Learn more about [sorting](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#sorting). 
  @maxSize: 10 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonSearchWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonSearchWithEntityContext['sort']>[number]['order'];
  }[];
  /** 
  Logical groupings of data into facets, with summaries for each facet. For example, use aggregations to allow site visitors to narrow down their search results by selecting specific categories. 
  @maxSize: 10 
  */
  aggregations?: {
    /** 
  A value aggregation calculates metrics such as count for specific fields within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number (count) of products for each price listed in the store.  
  */
    value?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['value'];
    /** 
  A range aggregation calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. For example, use a range aggregation to compare the number of reservations made for parties of 4 or less to the number of reservations made for parties with 5 or more.  
  */
    range?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['range'];
    /** 
  A scalar aggregation calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`.  
  */
    scalar?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['scalar'];
    /** 
  A date histogram calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.) For example, use a date histogram to find how many reservations have been made at a restaurant each week.  
  */
    dateHistogram?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['dateHistogram'];
    /** 
  A nested aggregation is applied within the results of another aggregation. Rather than aggregating directly on the primary dataset, first group data using one aggregation and then apply another aggregation within each group. It allows for more complex analyses where you can summarize data at different levels of detail or hierarchy. For example, to get the number of products that are in stock and out of stock for each price listed, first perform a value aggregation on `discountedPriceNumeric`, and a second value aggregation on `inStock`.  
  */
    nested?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['nested'];
    /** 
  Aggregation name, returned in `aggregations.results.name`. 
  @maxLength: 100 
  */
    name?:
      | NonNullable<
          CommonSearchWithEntityContext['aggregations']
        >[number]['name']
      | null;
    /** 
  Type of aggregation to perform. Must align with the corresponding aggregation field.  
  */
    type?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['type'];
    /** 
  Field to aggregate by. Use dot notation to specify a JSON path. For example, `order.address.streetName`. 
  @maxLength: 200 
  */
    fieldPath?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['fieldPath'];
    /** 
  Deprecated. Use `nested` instead. 
  @deprecated: Deprecated. Use `nested` instead.,
  @replacedBy: kind.nested,
  @targetRemovalDate: 2024-03-30 
  */
    groupBy?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['groupBy'];
  }[];
  /** 
  Free text to match in searchable fields.  
  */
  search?: {
    /** 
  Search mode. Defines the search logic for combining multiple terms in the `expression`.  
  */
    mode?: NonNullable<CommonSearchWithEntityContext['search']>['mode'];
    /** 
  Search term or expression. 
  @maxLength: 100 
  */
    expression?:
      | NonNullable<CommonSearchWithEntityContext['search']>['expression']
      | null;
    /** 
  Fields to search in. If the array is empty, all searchable fields are searched. Use dot notation to specify a JSON path. For example, `order.address.streetName`. 
  @maxLength: 200,
  @maxSize: 20 
  */
    fields?: NonNullable<CommonSearchWithEntityContext['search']>['fields'];
    /** 
  Whether to enable the search function to use an algorithm to automatically find results that are close to the search expression, such as typos and declensions.  
  */
    fuzzy?: NonNullable<CommonSearchWithEntityContext['search']>['fuzzy'];
  };
  /** 
  Time zone to adjust date-time-based filters and aggregations, in ISO 8601 (including offsets) or IANA time zone database (including time zone IDs) format.
  Applies to all relevant filters and aggregations, unless overridden by providing timestamps including time zone. For example, "2023-12-20T10:52:34.795Z". 
  @maxLength: 50 
  */
  timeZone?: CommonSearchWithEntityContext['timeZone'] | null;
};

/**
 * Decrements quantities of multiple inventory items.
 *
 * > **Notes:**:
 * > + `trackQuantity` must be `true` to allow for decrementing the quantity.
 * > + If you pass `restrictInventory: true` and the `decrementData.decrementBy` amount is greater than the current quantity in stock, the request will fail with an `INSUFFICIENT_INVENTORY` error.
 * > + Pass `restrictInventory: false` to allow for negative quantities.
 * > + If you pass `preorderRequest: true` and the item is available for preorder, the item's `preorderCounter` will increase and the item's quantity will stay the same.
 * @param decrementData - Inventory item IDs and decrement data.
 * @public
 * @requiredField decrementData
 * @requiredField decrementData.decrementBy
 * @requiredField decrementData.inventoryItemId
 * @permissionId WIX_STORES.INVENTORY_UPDATE
 * @applicableIdentity APP
 * @fqn com.wix.stores.catalog.inventory.api.v3.InventoryService.BulkDecrementInventoryItems
 */
export async function bulkDecrementInventoryItems(
  decrementData: NonNullablePaths<
    DecrementDataById,
    `decrementBy` | `inventoryItemId`,
    2
  >[],
  options?: BulkDecrementInventoryItemsOptions
): Promise<
  NonNullablePaths<
    BulkDecrementInventoryItemsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.inStock`
    | `results.${number}.item.quantity`
    | `results.${number}.item.variantId`
    | `results.${number}.item.productId`
    | `results.${number}.item.trackQuantity`
    | `results.${number}.item.availabilityStatus`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  > & {
    __applicationErrorsType?: BulkDecrementInventoryItemsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    decrementData: decrementData,
    restrictInventory: options?.restrictInventory,
    returnEntity: options?.returnEntity,
    reason: options?.reason,
  });

  const reqOpts =
    ambassadorWixStoresCatalogV3InventoryItem.bulkDecrementInventoryItems(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          decrementData: '$[0]',
          restrictInventory: '$[1].restrictInventory',
          returnEntity: '$[1].returnEntity',
          reason: '$[1].reason',
        },
        singleArgumentUnchanged: false,
      },
      ['decrementData', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkDecrementInventoryItemsOptions {
  /**
   * Whether inventory is restricted from going below zero.
   *
   * Default: `true` - inventory does not go below zero.
   */
  restrictInventory?: boolean | null;
  /**
   * Whether to return the full inventory item entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
  /** Reason for decrement inventory action. */
  reason?: ReasonTypeWithLiterals;
}

/**
 * Increments quantities of multiple inventory items.
 *
 * > **Note:** `trackQuantity` must be `true` to allow for incrementing the quantity.
 * @param incrementData - Inventory item IDs and increment data.
 * @public
 * @requiredField incrementData
 * @requiredField incrementData.incrementBy
 * @requiredField incrementData.inventoryItemId
 * @permissionId WIX_STORES.INVENTORY_UPDATE
 * @applicableIdentity APP
 * @fqn com.wix.stores.catalog.inventory.api.v3.InventoryService.BulkIncrementInventoryItems
 */
export async function bulkIncrementInventoryItems(
  incrementData: NonNullablePaths<
    IncrementDataById,
    `incrementBy` | `inventoryItemId`,
    2
  >[],
  options?: BulkIncrementInventoryItemsOptions
): Promise<
  NonNullablePaths<
    BulkIncrementInventoryItemsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.inStock`
    | `results.${number}.item.quantity`
    | `results.${number}.item.variantId`
    | `results.${number}.item.productId`
    | `results.${number}.item.trackQuantity`
    | `results.${number}.item.availabilityStatus`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  > & {
    __applicationErrorsType?: BulkIncrementInventoryItemsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    incrementData: incrementData,
    returnEntity: options?.returnEntity,
    reason: options?.reason,
  });

  const reqOpts =
    ambassadorWixStoresCatalogV3InventoryItem.bulkIncrementInventoryItems(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          incrementData: '$[0]',
          returnEntity: '$[1].returnEntity',
          reason: '$[1].reason',
        },
        singleArgumentUnchanged: false,
      },
      ['incrementData', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkIncrementInventoryItemsOptions {
  /**
   * Whether to return the full inventory item entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
  /** Reason for increment inventory action. */
  reason?: ReasonTypeWithLiterals;
}

/**
 * Decrements quantities of multiple inventory items by variant and location.
 *
 * > **Notes:**:
 * > + `trackQuantity` must be `true` to allow for decrementing the quantity.
 * > + If you pass `restrictInventory: true` and the `decrementData.decrementBy` amount is greater than the current quantity in stock, the request will fail with an `INSUFFICIENT_INVENTORY` error.
 * > + Pass `restrictInventory: false` to allow for negative quantities.
 * > + If you pass `preorderRequest: true` and the item is available for preorder, the item's `preorderCounter` will increase and the item's quantity will stay the same.
 * @param decrementData - Variant and location IDs, as well as decrement data.
 * @public
 * @requiredField decrementData
 * @requiredField decrementData.decrementBy
 * @requiredField decrementData.variantId
 * @permissionId WIX_STORES.INVENTORY_UPDATE
 * @applicableIdentity APP
 * @fqn com.wix.stores.catalog.inventory.api.v3.InventoryService.BulkDecrementInventoryItemsByVariantAndLocation
 */
export async function bulkDecrementInventoryItemsByVariantAndLocation(
  decrementData: NonNullablePaths<
    DecrementDataByVariantAndLocation,
    `decrementBy` | `variantId`,
    2
  >[],
  options?: BulkDecrementInventoryItemsByVariantAndLocationOptions
): Promise<
  NonNullablePaths<
    BulkDecrementInventoryItemsByVariantAndLocationResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.inStock`
    | `results.${number}.item.quantity`
    | `results.${number}.item.variantId`
    | `results.${number}.item.productId`
    | `results.${number}.item.trackQuantity`
    | `results.${number}.item.availabilityStatus`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  > & {
    __applicationErrorsType?: BulkDecrementInventoryItemsByVariantAndLocationApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    decrementData: decrementData,
    restrictInventory: options?.restrictInventory,
    returnEntity: options?.returnEntity,
    reason: options?.reason,
  });

  const reqOpts =
    ambassadorWixStoresCatalogV3InventoryItem.bulkDecrementInventoryItemsByVariantAndLocation(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          decrementData: '$[0]',
          restrictInventory: '$[1].restrictInventory',
          returnEntity: '$[1].returnEntity',
          reason: '$[1].reason',
        },
        singleArgumentUnchanged: false,
      },
      ['decrementData', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkDecrementInventoryItemsByVariantAndLocationOptions {
  /**
   * Whether to allow negative inventory following this decrement action.
   *
   * Default: `false` (negative inventory is not allowed)
   */
  restrictInventory?: boolean | null;
  /**
   * Whether to return the full inventory item entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
  /** Reason for decrement inventory action. */
  reason?: ReasonTypeWithLiterals;
}

/**
 * Increments quantities of multiple inventory items by variant and location.
 *
 * > **Note:** `trackQuantity` must be `true` to allow for incrementing the quantity.
 * @param incrementData - Variant and location IDs, as well as increment data.
 * @public
 * @requiredField incrementData
 * @requiredField incrementData.incrementBy
 * @requiredField incrementData.variantId
 * @permissionId WIX_STORES.INVENTORY_UPDATE
 * @applicableIdentity APP
 * @fqn com.wix.stores.catalog.inventory.api.v3.InventoryService.BulkIncrementInventoryItemsByVariantAndLocation
 */
export async function bulkIncrementInventoryItemsByVariantAndLocation(
  incrementData: NonNullablePaths<
    IncrementDataByVariantAndLocation,
    `incrementBy` | `variantId`,
    2
  >[],
  options?: BulkIncrementInventoryItemsByVariantAndLocationOptions
): Promise<
  NonNullablePaths<
    BulkIncrementInventoryItemsByVariantAndLocationResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.inStock`
    | `results.${number}.item.quantity`
    | `results.${number}.item.variantId`
    | `results.${number}.item.productId`
    | `results.${number}.item.trackQuantity`
    | `results.${number}.item.availabilityStatus`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  > & {
    __applicationErrorsType?: BulkIncrementInventoryItemsByVariantAndLocationApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    incrementData: incrementData,
    returnEntity: options?.returnEntity,
    reason: options?.reason,
  });

  const reqOpts =
    ambassadorWixStoresCatalogV3InventoryItem.bulkIncrementInventoryItemsByVariantAndLocation(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          incrementData: '$[0]',
          returnEntity: '$[1].returnEntity',
          reason: '$[1].reason',
        },
        singleArgumentUnchanged: false,
      },
      ['incrementData', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkIncrementInventoryItemsByVariantAndLocationOptions {
  /**
   * Whether to return the full inventory item entities in the response.
   *
   * Default: `false`
   */
  returnEntity?: boolean;
  /** Reason for increment inventory action. */
  reason?: ReasonTypeWithLiterals;
}

/**
 * Set or Adjust inventory items which satisfy the provided filter.
 * If item track quantity, adjust it's quantity,
 * In case non-tracking, updates it to track and sets quantity to `incrementBy` or `decrementBy`.
 * @param filter - Filter object in the following format:
 * `"filter" : {
 * "fieldName1": "value1",
 * "fieldName2":{"$operator":"value2"}
 * }`
 * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
 * @internal
 * @documentationMaturity preview
 * @requiredField filter
 * @requiredField options.adjustedInventoryAction
 * @permissionId WIX_STORES.INVENTORY_UPDATE
 * @applicableIdentity APP
 * @fqn com.wix.stores.catalog.inventory.api.v3.InventoryService.BulkSetOrAdjustInventoryItemsByFilter
 * @deprecated
 * @replacedBy com.wix.stores.catalog.inventory.api.v3.InventoryService.BulkAdjustInventoryItemsByFilter
 */
export async function bulkSetOrAdjustInventoryItemsByFilter(
  filter: Record<string, any>,
  options?: NonNullablePaths<
    BulkSetOrAdjustInventoryItemsByFilterOptions,
    `adjustedInventoryAction`,
    2
  >
): Promise<
  NonNullablePaths<
    BulkSetOrAdjustInventoryItemsByFilterResponse,
    `jobId`,
    2
  > & {
    __applicationErrorsType?: BulkSetOrAdjustInventoryItemsByFilterApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: filter,
    adjustedInventoryAction: options?.adjustedInventoryAction,
    search: options?.search,
  });

  const reqOpts =
    ambassadorWixStoresCatalogV3InventoryItem.bulkSetOrAdjustInventoryItemsByFilter(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          filter: '$[0]',
          adjustedInventoryAction: '$[1].adjustedInventoryAction',
          search: '$[1].search',
        },
        singleArgumentUnchanged: false,
      },
      ['filter', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkSetOrAdjustInventoryItemsByFilterOptions {
  /** Action to adjust the inventory by. (e.g increment or decrement) */
  adjustedInventoryAction: AdjustInventoryAction;
  /** Free text to match in searchable fields */
  search?: SearchDetails;
}

/**
 * Adjust quantity of inventory items which satisfy the provided filter.
 * For items that track quantity: Adjusts the quantity based on incrementBy or decrementBy.
 * For items that do not track quantity:
 * if track_quantity is false (default), found items that are non-tracking will fail.
 * if track_quantity is true, non-tracking items will be updated to track quantity and quantity will be set to the provided amount.
 * @param filter - Filter object in the following format:
 * `"filter" : {
 * "fieldName1": "value1",
 * "fieldName2":{"$operator":"value2"}
 * }`
 * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
 * @internal
 * @documentationMaturity preview
 * @requiredField filter
 * @requiredField options.adjustment
 * @permissionId WIX_STORES.INVENTORY_UPDATE
 * @applicableIdentity APP
 * @fqn com.wix.stores.catalog.inventory.api.v3.InventoryService.BulkAdjustInventoryItemsByFilter
 */
export async function bulkAdjustInventoryItemsByFilter(
  filter: Record<string, any>,
  options?: NonNullablePaths<
    BulkAdjustInventoryItemsByFilterOptions,
    `adjustment`,
    2
  >
): Promise<
  NonNullablePaths<BulkAdjustInventoryItemsByFilterResponse, `jobId`, 2> & {
    __applicationErrorsType?: BulkAdjustInventoryItemsByFilterApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: filter,
    adjustment: options?.adjustment,
    search: options?.search,
    trackQuantity: options?.trackQuantity,
  });

  const reqOpts =
    ambassadorWixStoresCatalogV3InventoryItem.bulkAdjustInventoryItemsByFilter(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          filter: '$[0]',
          adjustment: '$[1].adjustment',
          search: '$[1].search',
          trackQuantity: '$[1].trackQuantity',
        },
        singleArgumentUnchanged: false,
      },
      ['filter', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkAdjustInventoryItemsByFilterOptions {
  /** Action to adjust the inventory by (e.g. increment or decrement). */
  adjustment: AdjustInventoryAction;
  /** Free text to match in searchable fields. */
  search?: SearchDetails;
  /** If set to `true`, will set track method to track quantity. Default: `false`. */
  trackQuantity?: boolean;
}

/**
 * Creates inventory items at a target location for variants matching a filter.
 *
 * Uses variants reader to filter items by criteria (e.g. categories) and creates
 * inventory items at the specified location for each matching variant.
 *
 * The operation runs asynchronously. Use the returned job ID to track progress.
 * @param filter - Filter for variants. See [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language)
 * for WQL syntax and [Search Variants](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/product-variants-reader/search-variants)
 * for available filter fields.
 * @internal
 * @documentationMaturity preview
 * @requiredField filter
 * @requiredField options.inventoryCreationData.quantity
 * @requiredField options.locationId
 * @permissionId WIX_STORES.INVENTORY_CREATE
 * @applicableIdentity APP
 * @fqn com.wix.stores.catalog.inventory.api.v3.InventoryService.BulkCreateInventoryItemsByVariantFilter
 */
export async function bulkCreateInventoryItemsByVariantFilter(
  filter: Record<string, any>,
  options?: NonNullablePaths<
    BulkCreateInventoryItemsByVariantFilterOptions,
    `inventoryCreationData.quantity` | `locationId`,
    3
  >
): Promise<
  NonNullablePaths<BulkCreateInventoryItemsByVariantFilterResponse, `jobId`, 2>
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: filter,
    search: options?.search,
    locationId: options?.locationId,
    inventoryCreationData: options?.inventoryCreationData,
  });

  const reqOpts =
    ambassadorWixStoresCatalogV3InventoryItem.bulkCreateInventoryItemsByVariantFilter(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          filter: '$[0]',
          search: '$[1].search',
          locationId: '$[1].locationId',
          inventoryCreationData: '$[1].inventoryCreationData',
        },
        singleArgumentUnchanged: false,
      },
      ['filter', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkCreateInventoryItemsByVariantFilterOptions {
  /**
   * Free text to match in searchable fields.
   * When both `filter` and `search` are provided, results must satisfy both conditions (AND logic).
   * Use `filter` for precise field-based criteria and `search` for broad text matching.
   */
  search?: SearchDetails;
  /**
   * Target location ID where inventory items will be created.
   * @format GUID
   */
  locationId: string;
  /** Inventory data for created inventory items. */
  inventoryCreationData?: InventoryCreationData;
}
